import { useCallback, useState } from 'react'
import { ISelected } from '@vacationtracker/shared/components/filter-advanced/types'

import { IUsersFilter } from '../types'

const USERS_FILTERS_STORAGE_SUFFIX = 'usersFilters'

export type FilterDimension = 'Locations' | 'Departments' | 'Labels'

export const DIMENSION_TO_FIELD: Record<FilterDimension, 'locationIds' | 'teamIds' | 'labelIds'> = {
  Locations: 'locationIds',
  Departments: 'teamIds',
  Labels: 'labelIds',
}

export function hasActiveDimensionFilters(filters: IUsersFilter): boolean {
  return Boolean(filters.locationIds?.length || filters.teamIds?.length || filters.labelIds?.length || filters.name)
}

const storageKey = (userId: string) => `${userId}#${USERS_FILTERS_STORAGE_SUFFIX}`

function readStoredSelected(userId: string): ISelected[] {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) {
      return []
    }
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter((entry) => entry && typeof entry === 'object' && typeof entry.type === 'string' && Array.isArray(entry.values))
  } catch {
    return []
  }
}

function writeStoredSelected(userId: string, filters: IUsersFilter): void {
  const selected: ISelected[] = []
  if (filters.locationIds?.length) {
    selected.push({ type: 'Locations', values: filters.locationIds })
  }
  if (filters.teamIds?.length) {
    selected.push({ type: 'Departments', values: filters.teamIds })
  }
  if (filters.labelIds?.length) {
    selected.push({ type: 'Labels', values: filters.labelIds })
  }
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(selected.length ? selected : [{}]))
  } catch {
    return
  }
}

function syncUrl(pathname: string, filters: IUsersFilter, saveFilters: boolean): void {
  let searchParams = ''
  if (!saveFilters) {
    searchParams = '&saveFilters=false'
  }
  if (filters.locationIds?.length) {
    searchParams += `&locations=${filters.locationIds.join(',')}`
  }
  if (filters.teamIds?.length) {
    searchParams += `&departments=${filters.teamIds.join(',')}`
  }
  if (filters.labelIds?.length) {
    searchParams += `&labels=${filters.labelIds.join(',')}`
  }
  window.history.pushState({}, '', pathname + searchParams.replace('&', '?'))
}

export interface IUseUsersFiltersParams {
  userId: string
  pathname: string
  search: string
  saveFilters: boolean
}

export interface IUseUsersFiltersResult {
  filters: IUsersFilter
  setStatus: (status: string) => void
  setName: (name: string) => void
  setDimension: (dimension: FilterDimension, values: string[]) => void
  clearAll: () => void
  hasActiveDimensionFilters: boolean
}

export function useUsersFilters(params: IUseUsersFiltersParams): IUseUsersFiltersResult {
  const { userId, pathname, search, saveFilters } = params

  const [filters, setFilters] = useState<IUsersFilter>(() => {
    const urlParams = new URLSearchParams(search)
    const fromUrl: Partial<Record<FilterDimension, string[]>> = {
      Locations: urlParams.get('locations')?.split(','),
      Departments: urlParams.get('departments')?.split(','),
      Labels: urlParams.get('labels')?.split(','),
    }
    const hasUrlFilters = Boolean(fromUrl.Locations || fromUrl.Departments || fromUrl.Labels)
    const stored = !hasUrlFilters && saveFilters ? readStoredSelected(userId) : []
    const fromStorage = (dimension: FilterDimension) => stored.find((entry) => entry.type === dimension)?.values || []

    return {
      status: 'ACTIVE',
      name: '',
      locationIds: fromUrl.Locations || fromStorage('Locations'),
      teamIds: fromUrl.Departments || fromStorage('Departments'),
      labelIds: fromUrl.Labels || fromStorage('Labels'),
    }
  })

  const setStatus = useCallback((status: string) => {
    setFilters((previous) => (previous.status === status ? previous : { ...previous, status }))
  }, [])

  const setName = useCallback((name: string) => {
    setFilters((previous) => (previous.name === name ? previous : { ...previous, name }))
  }, [])

  const setDimension = useCallback(
    (dimension: FilterDimension, values: string[]) => {
      setFilters((previous) => {
        const next = { ...previous, [DIMENSION_TO_FIELD[dimension]]: values }
        if (saveFilters) {
          writeStoredSelected(userId, next)
        }
        syncUrl(pathname, next, saveFilters)
        return next
      })
    },
    [userId, pathname, saveFilters]
  )

  const clearAll = useCallback(() => {
    setFilters((previous) => {
      const next = { ...previous, name: '', locationIds: [], teamIds: [], labelIds: [] }
      if (saveFilters) {
        writeStoredSelected(userId, next)
      }
      syncUrl(pathname, next, saveFilters)
      return next
    })
  }, [userId, pathname, saveFilters])

  return { filters, setStatus, setName, setDimension, clearAll, hasActiveDimensionFilters: hasActiveDimensionFilters(filters) }
}
